import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Clock, CheckCircle, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MorningStretchGuide() {
  const navigate = useNavigate();

  const stretches = [
    {
      name: "Neck Rolls",
      duration: "1 minute",
      instructions: [
        "Stand or sit tall with shoulders relaxed",
        "Slowly drop your right ear toward your right shoulder",
        "Roll your chin down toward your chest",
        "Continue to your left shoulder, then back",
        "Repeat 3 times in each direction"
      ],
      benefit: "Releases tension from sleep position and screen time"
    },
    {
      name: "Cat-Cow Stretch",
      duration: "1 minute",
      instructions: [
        "Start on hands and knees, wrists under shoulders, knees under hips",
        "INHALE: Drop belly, lift chest and tailbone (Cow)",
        "EXHALE: Round spine toward ceiling, tuck chin and tailbone (Cat)",
        "Move slowly with your breath",
        "Repeat 8-10 times"
      ],
      benefit: "Warms up the spine and relieves lower back stiffness"
    },
    {
      name: "Child's Pose",
      duration: "1 minute",
      instructions: [
        "From hands and knees, sit hips back toward heels",
        "Extend arms forward on the floor",
        "Rest forehead on the mat",
        "Breathe deeply into your lower back",
        "Hold for 5-6 deep breaths"
      ],
      benefit: "Gently stretches hips, thighs, and ankles; calms the mind"
    },
    {
      name: "Standing Forward Fold",
      duration: "1 minute",
      instructions: [
        "Stand with feet hip-width apart",
        "Hinge at hips and fold forward, bending knees if needed",
        "Let head hang heavy, grab opposite elbows",
        "Gently sway side to side",
        "Hold for 5-6 breaths, slowly roll up to standing"
      ],
      benefit: "Stretches hamstrings and releases spine compression"
    },
    {
      name: "Hip Flexor Lunge",
      duration: "2 minutes (1 min each side)",
      instructions: [
        "Step right foot forward into a lunge, left knee on floor",
        "Keep right knee over ankle, not past toes",
        "Tuck tailbone slightly and lean forward into front hip",
        "Raise arms overhead for deeper stretch",
        "Hold 30-60 seconds, switch sides"
      ],
      benefit: "Counteracts sitting all day; opens tight hip flexors"
    },
    {
      name: "Seated Spinal Twist",
      duration: "2 minutes (1 min each side)",
      instructions: [
        "Sit with legs extended, bend right knee and cross over left leg",
        "Place right hand behind you, left elbow outside right knee",
        "Inhale to lengthen spine, exhale to twist deeper",
        "Look over right shoulder",
        "Hold 30-60 seconds, switch sides"
      ],
      benefit: "Improves spinal mobility and aids digestion"
    },
    {
      name: "Chest Opener",
      duration: "1 minute",
      instructions: [
        "Stand or sit tall, interlace fingers behind your back",
        "Straighten arms and lift hands away from body",
        "Open chest, squeeze shoulder blades together",
        "Lift chin slightly without straining neck",
        "Hold for 5-6 deep breaths"
      ],
      benefit: "Counteracts hunched posture from phones and computers"
    },
    {
      name: "Figure-4 Stretch",
      duration: "2 minutes (1 min each side)",
      instructions: [
        "Lie on back, cross right ankle over left knee",
        "Thread right hand through legs, clasp behind left thigh",
        "Gently pull left thigh toward chest",
        "Keep right knee pressing away from you",
        "Hold 30-60 seconds, switch sides"
      ],
      benefit: "Releases tight glutes and piriformis (helps with sciatic pain)"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Week 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            10-Minute Morning Stretch Routine
          </h1>
          <p className="text-xl text-secondary-600">
            Wake Up Your Body & Set the Tone for Your Day
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/dff757c22f1f5e704bcddbd74cdde2d0376392012662d55b4042fd5e0b8301e3.png"
            alt="Morning Stretching"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Morning Stretching Matters
          </h2>
          <p className="text-secondary-700 mb-4">
            After 7-8 hours of sleep, your muscles are stiff, your spine is compressed, and your body craves movement. Morning stretching increases blood flow, releases tension, and sends oxygen to your brain—helping you feel alert and energized without caffeine.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { title: "Improves Flexibility", description: "Muscles are most receptive to stretching when warm from sleep" },
              { title: "Reduces Pain", description: "Prevents the aches that come from staying sedentary" },
              { title: "Boosts Energy", description: "Better than coffee for waking up your body and mind" }
            ].map((item, index) => (
              <div key={index} className="text-center p-4 bg-teal-50 rounded-xl">
                <h3 className="font-bold text-teal-700 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-secondary-600">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Overview */}
        <section className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">Total Time: 10 Minutes</p>
                <p className="text-teal-100 text-sm">8 stretches, head to toe</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Repeat className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">Do It Daily</p>
                <p className="text-teal-100 text-sm">Right after waking up</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stretches */}
        <section className="space-y-6 mb-6">
          {stretches.map((stretch, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-bold">
                    {index + 1}
                  </span>
                  <h3 className="text-xl font-bold text-secondary-900">{stretch.name}</h3>
                </div>
                <span className="text-sm bg-teal-50 text-teal-700 px-3 py-1 rounded-full">
                  {stretch.duration}
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-2 text-sm">How to do it:</h4>
                  <ul className="space-y-2">
                    {stretch.instructions.map((instruction, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-secondary-700">
                        <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        {instruction}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-teal-50 rounded-xl p-4 flex items-center">
                  <div>
                    <h4 className="font-semibold text-teal-800 mb-1 text-sm">Why it helps:</h4>
                    <p className="text-secondary-700 text-sm">{stretch.benefit}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Tips */}
        <section className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border-2 border-teal-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Tips for Success
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Don't bounce", tip: "Hold stretches steady. Bouncing can cause muscle tears." },
              { title: "Breathe deeply", tip: "Exhale as you deepen each stretch. Breath helps muscles relax." },
              { title: "Listen to your body", tip: "Stretch to mild discomfort, never pain. Back off if it hurts." },
              { title: "Stay consistent", tip: "10 minutes daily beats 60 minutes once a week." },
              { title: "Warm up first", tip: "March in place for 30 seconds before starting if you feel very stiff." },
              { title: "Make it a ritual", tip: "Same time, same place, same routine. Let it become automatic." }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-bold text-teal-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MorningStretchGuide;
